<template>
  <div class="h-[100dvh] bg-pitch-black text-total-white flex flex-col">
    <TopNavbar />

    <div
      class="bg-eerie-black"
      :class="{ 'opacity-40': isMessageSending, 'pointer-events-none': isMessageSending }"
    >
      <!-- Header Section -->
      <div class="flex justify-between px-4 pt-4 items-center mb-3">
        <h1 class="text-xl font-semibold">New Text Message</h1>
        <div class="flex space-x-4">
          <button @click="cancelText" class="text-prime-golden text-lg">Cancel</button>
        </div>
      </div>

      <!-- Recipient Field with Crossable Tags and Dropdown -->
      <div
        class="flex bg-eerie-black gap-1 border-y border-dark-grey items-center h-10 px-4"
      >
        <label for="to" class="block text-sm text-total-white mr-2">To:</label>
        <div class="flex items-center flex-1 gap-2">
          <div
            v-if="recipients.length > 0"
            class="flex items-center bg-dark-grey rounded-md px-2 py-1"
          >
            <span class="text-sm whitespace-nowrap text-prime-golden">
              {{
                recipients[0].first_name
                  ? recipients[0].first_name + " " + recipients[0].last_name
                  : recipients[0].phone
              }}
            </span>
            <button @click="removeRecipient" class="text-sm ml-2 text-prime-golden">
              <i class="fa fa-close"></i>
            </button>
          </div>

          <!-- Input for selecting contacts -->
          <div class="relative w-full" ref="recipientDropdown">
            <input
              id="to"
              v-model="recipientInput"
              @keydown.enter.prevent="addRecipient"
              @blur="onInputBlur"
              :disabled="recipients.length > 0"
              class="bg-transparent text-sm font-semibold text-prime-golden outline-none w-full"
              type="text"
              :placeholder="recipients.length < 1 ? 'Add recipient' : ''"
              @focus="showDropdown = true"
            />
            <ul
              v-if="showDropdown && filteredContacts && filteredContacts.length > 0"
              class="absolute z-10 bg-eerie-black border border-dark-grey w-full max-h-40 overflow-y-auto rounded-md"
            >
              <li
                v-for="contact in filteredContacts"
                :key="contact.id"
                @click="selectRecipient(contact)"
                class="cursor-pointer px-4 py-2 border-b border-light-grey-translucent text-total-white"
              >
                <span>{{ getContactName(contact) }}</span>
              </li>
            </ul>
          </div>
        </div>
      </div>

      <!-- Project Dropdown -->
      <div
        :class="[
          'flex relative items-center border-b border-dark-grey gap-1 px-4',
          dropdownOpen ? 'bg-dark-grey py-4' : 'bg-eerie-black py-2',
        ]"
        ref="projectDropdown"
      >
        <label class="block text-total-white">Project:</label>
        <div class="relative w-full bg-transparent px-2 flex items-center">
          <div
            v-if="!dropdownOpen"
            @click="toggleDropdown"
            class="w-full flex justify-between items-center cursor-pointer"
          >
            <span class="text-sm font-semibold text-total-white">{{
              selectedProject?.address_1 || "Select a project"
            }}</span>
            <i class="fas fa-chevron-down text-prime-golden"></i>
          </div>
          <div v-else class="w-full flex items-center">
            <input
              v-model="searchQuery"
              type="text"
              placeholder="Search projects..."
              class="w-full bg-transparent border-none text-total-white outline-none"
            />
            <button
              @click="closeSearch"
              class="text-xs text-pitch-black px-1 rounded-full bg-ash-grey ml-2"
            >
              <i class="fas fa-times"></i>
            </button>
          </div>
        </div>
        <div
          v-if="dropdownOpen"
          class="absolute top-12 left-0 w-full bg-eerie-black border border-dark-grey mt-2 z-10"
        >
          <ul>
            <li
              v-for="project in filteredProjects"
              :key="project.id"
              @click="selectProject(project)"
              class="p-4 bg-charcoal-grey border-b border-light-grey-translucent-dark hover:bg-dark-grey cursor-pointer"
            >
              {{ project.address_1 }}
            </li>
          </ul>
        </div>
      </div>
    </div>

    <!-- Progress Bar -->
    <div
      class="h-1 bg-prime-golden transition-all duration-300 w-full"
      :style="{ width: progressBarWidth }"
    ></div>

    <!-- Chat Messages / Spinner Area -->
    <div
      ref="messagesContainerRef"
      class="overflow-y-auto min-h-0 flex flex-1 flex-col px-4 py-4 pt-6 space-y-4"
    >
      <!-- Show spinner while fetching thread -->
      <div v-if="isFetchingThread" class="flex flex-1 items-center justify-center h-full">
        <img
          src="@/assets/images/spinner.svg"
          alt="Loading Spinner"
          class="text-prime-golden w-10 h-10"
        />
      </div>
      <!-- Otherwise, show messages if any -->
      <template v-else>
        <TextBubble
          v-for="(msg, index) in mappedMessages"
          :key="index"
          :side="msg.side"
          :avatar="msg.avatar || ''"
          :message="msg.message"
          :timestamp="msg.timestamp"
          :image="msg.image"
          :files="msg.files"
        />
      </template>
    </div>

    <!-- Message Input Section (unchanged) -->
    <div
      class="flex flex-wrap items-center px-3 py-2 mt-auto bg-eerie-black border-t border-dark-grey max-h-80"
      :class="{ 'opacity-40': isMessageSending, 'pointer-events-none': isMessageSending }"
    >
      <!-- Attachments Preview -->
      <div
        v-if="attachments.length > 0"
        class="py-2 mb-1 flex flex-wrap justify-start items-center gap-4 w-full"
      >
        <h3 class="text-sm font-semibold text-total-white">Attachments:</h3>
        <div class="flex flex-wrap gap-2">
          <div
            v-for="(file, index) in attachments"
            :key="index"
            class="text-md text-prime-golden bg-dark-grey px-2 py-1 rounded-lg flex items-center"
          >
            {{ file.name }}
            <button @click="removeAttachment(index)" class="ml-2 text-prime-golden">
              ×
            </button>
          </div>
        </div>
      </div>
      <!-- Attachments Button -->
      <button
        @click="triggerFileInput"
        class="text-total-white-500 w-8 h-8 flex items-center justify-center bg-light-grey-translucent px-1 rounded-full mr-3"
      >
        <i class="fas fa-plus text-base"></i>
      </button>
      <input
        type="file"
        ref="fileInput"
        class="hidden"
        @change="handleFileUpload"
        multiple
      />
      <div
        class="bg-pitch-black text-total-white rounded-full flex items-center px-2 border border-ash-grey flex-1 focus:rounded-xl"
        :class="{ 'rounded-xl': newMessageFocus }"
      >
        <textarea
          v-model="newMessage"
          placeholder="Enter Message..."
          @keydown.enter="sendMessage"
          @focus="newMessageFocus = true"
          class="flex-1 bg-pitch-black text-total-white py-1 px-2 rounded-full focus:outline-none resize-none"
          :class="{ 'h-20 py-4 rounded-sm': newMessageFocus }"
          :rows="newMessage ? newMessage.split('\n').length : 1"
        ></textarea>
        <button
          @click="sendMessage"
          class="bg-prime-golden text-pitch-black px-[0.4rem] rounded-full ml-2"
          :disabled="!canSendMessage || isMessageSending"
          :class="{
            'opacity-50 pointer-events-none': !canSendMessage || isMessageSending,
          }"
        >
          <i class="fas fa-arrow-up text-sm"></i>
        </button>
      </div>
    </div>
  </div>
</template>

<script setup>
import {
  ref,
  computed,
  onMounted,
  watch,
  onBeforeUnmount,
  watchEffect,
  nextTick,
} from "vue";
import { useRouter, useRoute } from "vue-router";
import TopNavbar from "@/components/TopNavbar.vue";
import TextBubble from "@/components/TextBubble.vue";
import { useLogStore } from "@/stores/log";
import { useContactsStore } from "@/stores/contact";
import { useProjectStore } from "@/stores/project";
import { useUserStore } from "@/stores/user";
import { convertHeicToPng } from "@/misc/util";
import { useToast } from "vue-toastification";

const route = useRoute();
const router = useRouter();
const toast = useToast();

const logStore = useLogStore();
const contactsStore = useContactsStore();
const projectStore = useProjectStore();
const userStore = useUserStore();

// Input, dropdown, and attachment refs/state
const recipientInput = ref("");
const recipients = ref([]);
const newMessage = ref("");
const dropdownOpen = ref(false);
const selectedProject = ref(
  Number(route.params.projectId || route.query.projectId) || null
);
const searchQuery = ref("");
const showDropdown = ref(false);
const attachments = ref([]);
const recipientDropdown = ref(null);
const projectDropdown = ref(null);
const fileInput = ref(null);

const isMessageSending = ref(false);
const progressBarWidth = ref("0%");
const newMessageFocus = ref(false);

const isFetchingThread = ref(false);
const threadIdNew = ref(null);
const isProjectReady = ref(false);

// Project manager phone – set via watch when project is selected
const projectManagerPhoneNumber = ref(null);

const messagesContainerRef = ref(null);

onMounted(async () => {
  await projectStore.fetchAllProjects();
  await userStore.fetchUserProfile();
  document.addEventListener("click", closeToDropdown);

  if (route.params.projectId || route.query.projectId) {
    await contactsStore.fetchContactsByProjectId(
      Number(route.params.projectId || route.query.projectId)
    );
  } else {
    await contactsStore.fetchAllContacts();
  }

  if (route.params.contactId) {
    const contact = contactsStore.contacts.find((c) => c.id == route.params.contactId);
    if (contact) recipients.value.push(contact);
  }
  if (route.params.projectId) {
    const project = projectStore.projects.find((p) => p.id == route.params.projectId);
    if (project) selectedProject.value = project;
  }
});

onBeforeUnmount(() => {
  document.removeEventListener("click", closeToDropdown);
});
function scrollToBottom() {
  if (!messagesContainerRef.value) return;
  messagesContainerRef.value.scrollTop = messagesContainerRef.value.scrollHeight;
}
// Update project manager phone when project changes
watch(selectedProject, async (newProject) => {
  if (!newProject) {
    isProjectReady.value = false;
    return;
  }
  try {
    await projectStore.fetchProjectById(newProject.id);
    projectManagerPhoneNumber.value =
      projectStore.currentProject?.project_manager_twilio_number?.phone_number || null;
    if (route.query.projectId && selectedProject) {
      await contactsStore.fetchContactsByProjectId(selectedProject.value.id);
    }
    isProjectReady.value = true;
  } catch (err) {
    console.error("Error fetching project or setting manager phone:", err);
    isProjectReady.value = false;
  }
});

// NEW: Watch for recipient and project changes to fetch thread
let findThreadDebounceTimeout = null;
watchEffect(() => {
  // Only proceed if the project is loaded,
  // we have at least 1 recipient, and selectedProject is not null
  if (
    !isProjectReady.value ||
    recipients.value.length === 0 ||
    !selectedProject.value ||
    !projectManagerPhoneNumber.value
  )
    return;

  // Clear any previous timeout
  if (findThreadDebounceTimeout) {
    clearTimeout(findThreadDebounceTimeout);
  }

  // Debounce the API call so that rapid changes only trigger one call
  findThreadDebounceTimeout = setTimeout(async () => {
    isFetchingThread.value = true;
    try {
      const foundThreadId = await logStore.findTextThread(selectedProject.value.id, [
        projectManagerPhoneNumber.value || "",
        ...recipients.value.map((r) => r.phone),
      ]);
      threadIdNew.value = foundThreadId || null;
      if (threadIdNew.value) {
        await logStore.fetchMessagesByThreadId(threadIdNew.value, "asc", 1);
      }
    } catch (error) {
      console.error("Error fetching thread:", error);
    } finally {
      isFetchingThread.value = false;
    }
  }, 200); // 200ms delay; adjust if needed
});

// Computed contacts and projects
const contacts = computed(() => contactsStore.contacts || []);
const projects = computed(() => projectStore.projects || []);
const filteredContacts = computed(() => {
  const query = recipientInput.value.toLowerCase();
  if (!contacts.value || !Array.isArray(contacts.value)) {
    return [];
  }
  return contacts.value.filter((contact) => {
    // Exclude the PM contact if applicable
    if (contact.role === "Project Manager") {
      return false;
    }
    
    // Add null checks for contact properties
    const firstName = contact.first_name || "";
    const lastName = contact.last_name || "";
    const phone = contact.phone || "";
    
    const contactName = `${firstName} ${lastName}`.toLowerCase();
    const contactPhone = phone.toLowerCase();
    
    return phone && (contactName.includes(query) || contactPhone.includes(query));
  });
});

const filteredProjects = computed(() => {
  if (!projects.value || !Array.isArray(projects.value)) {
    return [];
  }
  return projects.value.filter((project) =>
    project.address_1.toLowerCase().includes(searchQuery.value.toLowerCase())
  );
});

// Recipient helper methods
const addRecipient = () => {
  // Prevent adding if a recipient tag already exists
  if (recipients.value.length > 0) return;

  const input = recipientInput.value.trim();
  if (!input) return;

  // First, try to find a matching contact by name (case insensitive)
  let contact = filteredContacts.value && filteredContacts.value.find
    ? filteredContacts.value.find(
        (c) => {
          const firstName = c.first_name || "";
          const lastName = c.last_name || "";
          const fullName = `${firstName} ${lastName}`.trim().toLowerCase();
          return fullName === input.toLowerCase();
        }
      )
    : null;

  // If no contact found by name and input is a valid phone number, check by phone
  if (!contact && /^(\+)?\d+$/.test(input)) {
    let phoneNumber = input;
    // Prepend default country code if missing (US in this example)
    if (!phoneNumber.startsWith("+")) {
      phoneNumber = "+1" + phoneNumber;
    }
    // Check if a contact with this phone number exists in the dropdown contacts
    contact = filteredContacts.value && filteredContacts.value.find
      ? filteredContacts.value.find((c) => c.phone === phoneNumber)
      : null;
    console.log(contact, phoneNumber, filteredContacts);
    if (contact) {
      recipients.value = [contact];
    } else {
      // If not found, create a new recipient object with fallback names
      recipients.value = [
        {
          first_name: "Unknown",
          last_name: "Unknown",
          phone: phoneNumber,
        },
      ];
    }
  } else if (contact) {
    // If found by name, use that contact
    recipients.value = [contact];
  } else {
    toast.error("Invalid phone number or contact name.");
    return;
  }

  // Clear the input and hide the dropdown
  recipientInput.value = "";
  showDropdown.value = false;
};

const onInputBlur = () => {
  setTimeout(() => {
    addRecipient();
  }, 150); // 150ms delay, adjust if needed
};

const selectRecipient = (contact) => {
  recipients.value = [contact];
  recipientInput.value = "";
  showDropdown.value = false;
};
const removeRecipient = () => {
  recipients.value = [];
  logStore.threadMessages = [];
};
const getContactName = (contact) => {
  const firstName = contact.first_name || "";
  const lastName = contact.last_name || "";
  const fullName = `${firstName} ${lastName}`.trim();
  
  return fullName || contact.phone || "Unknown";
};

// Project dropdown methods
const selectProject = (project) => {
  selectedProject.value = project;
  dropdownOpen.value = false;
};
const toggleDropdown = () => {
  dropdownOpen.value = !dropdownOpen.value;
};
const closeSearch = () => {
  searchQuery.value = "";
  dropdownOpen.value = false;
};
const closeToDropdown = (event) => {
  if (recipientDropdown.value && !recipientDropdown.value.contains(event.target)) {
    showDropdown.value = false;
  }
};

// File input methods
const triggerFileInput = () => {
  fileInput.value.click();
};
const handleFileUpload = (event) => {
  const files = Array.from(event.target.files);
  attachments.value.push(...files);
};
const removeAttachment = (index) => {
  attachments.value.splice(index, 1);
};

// Cancel message and navigate back
const cancelText = () => {
  if (route.query.from_contact) {
    router.go(-1);
  } else if (route.params.projectId || route.query.projectId) {
    router.push({
      name: "ProjectPage",
      params: { id: route.params.projectId || route.query.projectId },
      query: { tab: route.query.currentTab || "contacts" },
    });
  } else {
    router.go(-1);
  }
};

// File encoding helper
const encodeFileToBytes = (file) => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onloadend = () => {
      const adjustedFileName =
        file.type === "image/heic" ? file.name.replace(/\.heic$/i, ".png") : file.name;
      const blob = new Blob([reader.result], {
        type: file.type === "image/heic" ? "image/png" : file.type,
        name: adjustedFileName,
      });
      resolve(blob);
    };
    reader.onerror = reject;
    reader.readAsArrayBuffer(file);
  });
};

// Computed: canSendMessage and mappedMessages (using threadIdNew)
const canSendMessage = computed(
  () => newMessage.value.trim() !== "" || attachments.value.length > 0
);
const participantMapping = computed(() => {
  if (!logStore.threadMessages.length) return {};
  // if (logStore.threadMessages[0].thread_id !== threadId) return {};

  const toParticipants =
    logStore.threadMessages[0]?.to_contact_stakeholders?.map((st) => ({
      first_name: st.first_name,
      id: st.id,
      phone: st.phone,
      role: st.role,
    })) || [];
  const fromParticipant = logStore.threadMessages[0]?.from_contact_stakeholder
    ? {
        first_name: logStore.threadMessages[0].from_contact_stakeholder.first_name,
        id: logStore.threadMessages[0].from_contact_stakeholder.id,
        phone: logStore.threadMessages[0].from_contact_stakeholder.phone,
        role: logStore.threadMessages[0].from_contact_stakeholder.role,
      }
    : null;

  // Combine participants. (If you previously reversed the order, remove that.)
  const parts = fromParticipant ? toParticipants.concat(fromParticipant) : toParticipants;

  let rightParticipant = null;

  // Rule 1: If a Project Manager (PM/self) is present, choose that.
  rightParticipant = parts.find((p) => p.role === "Project Manager");

  // Rule 2: Otherwise, if a Project Agent is present, choose that.
  if (!rightParticipant) {
    rightParticipant = parts.find((p) => p.role === "Project Agent");
  }

  // Rule 3: Otherwise, if exactly two participants exist, choose one alphabetically.
  if (!rightParticipant && parts.length === 2) {
    rightParticipant = parts
      .slice()
      .sort((a, b) => (a.first_name || "").localeCompare(b.first_name || ""))[0];
  }

  // Rule 4: Otherwise, if more than two exist, choose the alphabetically first.
  if (!rightParticipant && parts.length > 0) {
    rightParticipant = parts
      .slice()
      .sort((a, b) => (a.first_name || "").localeCompare(b.first_name || ""))[0];
  }

  // Build mapping: assign "right" to the chosen participant and "left" to all others.
  const mapping = {};
  if (rightParticipant) {
    mapping[rightParticipant.id] = "right";
    parts.forEach((p) => {
      if (p.id !== rightParticipant.id) {
        mapping[p.id] = "left";
      }
    });
  }
  return mapping;
});
const mappedMessages = computed(() => {
  if (!threadIdNew.value) return [];
  // Optionally include your existing participant mapping logic here.
  const mapping = participantMapping.value;
  return logStore.threadMessages.map((msg) => {
    let side = "left";
    if (msg.from_contact_stakeholder && mapping[msg.from_contact_stakeholder.id]) {
      side = mapping[msg.from_contact_stakeholder.id];
    }

    // Enhanced image detection logic
    let imageUrl = null;
    
    // Helper function to check if a file is an image
    const isImageFile = (file) => {
      if (!file) return false;
      
      // Check file_type first (server provided)
      if (file.file_type) {
        const fileType = file.file_type.toLowerCase();
        if (/(jpg|jpeg|png|gif|bmp|webp|svg|heic|heif|avif|tiff|tif)$/i.test(fileType)) {
          return true;
        }
      }
      
      // Check filename extension as fallback
      if (file.filename || file.name) {
        const filename = (file.filename || file.name).toLowerCase();
        if (/(jpg|jpeg|png|gif|bmp|webp|svg|heic|heif|avif|tiff|tif)$/i.test(filename)) {
          return true;
        }
      }
      
      // Check MIME type if available
      if (file.mime_type || file.type) {
        const mimeType = (file.mime_type || file.type).toLowerCase();
        if (mimeType.startsWith('image/')) {
          return true;
        }
      }
      
      return false;
    };

    // Check URL-based detection for media_urls
    const isImageUrl = (url) => {
      if (!url) return false;
      return /(jpg|jpeg|png|gif|bmp|webp|svg|heic|heif|avif|tiff|tif)$/i.test(url);
    };

    // First, try to find image from files array
    if (msg.files && msg.files.length > 0) {
      const imageFile = msg.files.find(isImageFile);
      if (imageFile) {
        imageUrl = imageFile.file_url || imageFile.url;
      }
    }

    // Fallback to media_urls if no image found in files
    if (!imageUrl && msg.media_urls && msg.media_urls.length > 0) {
      const mediaImage = msg.media_urls.find(isImageUrl);
      if (mediaImage) {
        imageUrl = mediaImage;
      }
    }

    // Filter out image files from the files list to avoid duplication
    const nonImageFiles = msg.files?.filter(file => !isImageFile(file)) || [];

    return {
      id: msg.id,
      side,
      message: msg.content,
      avatar: msg.from_contact_stakeholder?.profile_image_url,
      timestamp: new Date(msg.date_time_created).toLocaleString(),
      image: imageUrl,
      files: nonImageFiles,
    };
  });
});

watch(
  () => mappedMessages.value,
  async () => {
    await nextTick();

    setTimeout(() => {
      scrollToBottom();
    }, 50);
  }
);

// sendMessage remains largely unchanged
const sendMessage = async () => {
  if (!canSendMessage.value) return;
  if (!selectedProject.value) {
    toast.error("Please select a project before sending a message.");
    return;
  }
  isMessageSending.value = true;
  progressBarWidth.value = "10%";

  let danglingInput = recipientInput.value.trimEnd();
  let existingThreadId = null;

  if (danglingInput && /^(\+)?\d+$/.test(danglingInput)) {
    recipientInput.value = "";
    if (!danglingInput.startsWith("+")) {
      danglingInput = "+1" + danglingInput;
    }
    const existingContact = contacts.value.find(
      (contact) => contact.phone === danglingInput
    );
    if (existingContact) {
      recipients.value.push(existingContact);
      existingThreadId = await logStore.findTextThread(selectedProject.value.id, [
        projectManagerPhoneNumber.value,
        existingContact.phone || "",
      ]);
    } else {
      recipients.value.push({
        first_name: "Unknown",
        last_name: "Unknown",
        phone: danglingInput,
      });
    }
  } else {
    existingThreadId = await logStore.findTextThread(selectedProject.value.id, [
      projectManagerPhoneNumber.value,
      ...recipients.value.map((r) => r.phone || ""),
    ]);
  }

  progressBarWidth.value = "40%";

  if (newMessage.value.trim() && recipients.value.length > 0 && selectedProject.value) {
    const phoneNumbers = recipients.value.map((r) => r.phone || "").filter(phone => phone).join(",");
    const projectId = selectedProject.value.id;
    const messageData = {
      message: newMessage.value,
      to_phone_numbers: phoneNumbers,
      project_id: projectId,
      files: attachments.value,
    };

    try {
      const formData = new FormData();
      formData.append("message", messageData.message);
      formData.append("to_phone_numbers", messageData.to_phone_numbers);
      formData.append("project_id", messageData.project_id);
      if (existingThreadId) {
        formData.append("thread_id", existingThreadId);
        formData.delete("to_phone_numbers");
      }
      for (const file of messageData.files) {
        let fileAsBytes;
        if (file.type === "image/heic" || file.type === "image/heif") {
          const pngBlob = await convertHeicToPng(file);
          fileAsBytes = await encodeFileToBytes(pngBlob);
        } else {
          fileAsBytes = await encodeFileToBytes(file);
        }
        fileAsBytes.name =
          file.type === "image/heic" || file.type === "image/heif"
            ? file.name.replace(/\.(heic|heif)$/i, ".png")
            : file.name;
        formData.append("files", fileAsBytes, fileAsBytes.name.replace(/\s+/g, "_"));
      }

      const response = await logStore.sendTextMessage(formData);
      const threadId = response?.thread_id;
      progressBarWidth.value = "70%";
      newMessageFocus.value = false;
      // await logStore.fetchLogsByProjectId(projectId, null, null, "desc");
      // const latestLog = logStore.logs?.[0];
      // const threadId = existingThreadId || latestLog?.thread_id;
      newMessage.value = "";
      attachments.value = [];
      progressBarWidth.value = "100%";

      router.push({
        name: "TextPage",
        params: {
          projectId: projectId,
          threadId: threadId,
          contactId: route.params.contactId,
        },
        query: {
          thread_id: threadId,
          from_contact: route.query.from_contact,
          tab: route.query.currentTab,
        },
      });
    } catch (error) {
      console.error("Error sending message:", error);
    }
  } else {
    console.log("No message or recipients:", newMessage.value, recipients.value);
  }
};
</script>

<style scoped></style>
